/**
 * Live presence reporter — tells the backend what screen the driver is on right now
 * (+ the relevant job), so admin can see live activity on the driver's page.
 * Fire-and-forget; failures are ignored.
 */
import { api } from './api';
import { BUILD_VERSION, hasRealBuildVersion } from '../buildVersion';

// The build version THIS app is actually RUNNING. Prefer the baked-in BUILD_VERSION (stamped
// into the bundle at deploy time — ground truth that can't lie), and only fall back to the
// localStorage __app_reset flag on an unstamped/dev build. Reported on every heartbeat so the
// backend/admin can SEE which drivers are on the latest bundle vs stuck on an old one, and so
// the self-update comparison is honest (a stale reload can no longer make a device claim it's
// current while running old code).
export function loadedAppVersion(): string | undefined {
  if (hasRealBuildVersion()) return BUILD_VERSION;
  try {
    if (typeof localStorage !== 'undefined') return localStorage.getItem('__app_reset') || undefined;
  } catch { /* ignore */ }
  return undefined;
}

// Returns whether an admin is currently watching this driver's Live tab (gates the screen
// capture) plus the latest shipped app version (drives self-update). Best-effort.
export async function reportPresence(screen: string, jobId?: string | null): Promise<{ watched: boolean; latestVersion?: string }> {
  try {
    const { data } = await api.post('/drivers/me/presence', { screen, jobId: jobId || null, appVersion: loadedAppVersion() });
    return { watched: !!data?.watched, latestVersion: data?.latestVersion };
  } catch {
    /* ignore — presence is best-effort */
    return { watched: false };
  }
}
